import React, { useCallback, useState, useEffect, memo } from "react";
import { getOfferRewardDisplay } from "byzantine/src/dbbl/businessLogic/entities/cardLinkedOffers";
import { CardOfferTileProps } from "./types";
import styles from "./CardOfferScrollView.module.scss";

const LOGO_LOAD_TIMEOUT_MS = 2000 as const;

const CardOfferTile = ({ offer, onClick }: CardOfferTileProps) => {
  const [logoLoading, setLogoLoading] = useState(!!offer.logoUrl);

  // Reset loading state when offer logo URL changes
  useEffect(() => {
    if (offer.logoUrl) {
      setLogoLoading(true);

      // Fallback timeout to prevent infinite loading
      const timeout = setTimeout(() => {
        setLogoLoading(false);
      }, LOGO_LOAD_TIMEOUT_MS);

      return () => clearTimeout(timeout);
    }

    setLogoLoading(false);
    return undefined;
  }, [offer.logoUrl]);

  const handleClick = useCallback(() => {
    onClick?.(offer.id);
  }, [onClick, offer.id]);

  const handleLogoLoad = useCallback(() => {
    setLogoLoading(false);
  }, []);

  const handleLogoError = useCallback(() => {
    setLogoLoading(false);
  }, []);

  return (
    <div
      className={styles.offerCard}
      onClick={handleClick}
      data-testid={`offer-${offer.id}`}
    >
      <div className={styles.offerLogo}>
        {offer.logoUrl ? (
          <>
            <img
              src={offer.logoUrl}
              alt={offer.name}
              onLoad={handleLogoLoad}
              onError={handleLogoError}
              style={{ display: logoLoading ? "none" : "block" }}
            />
            {logoLoading && <div className={styles.offerLogoLoading} />}
          </>
        ) : (
          <div className={styles.placeholderLogo}>{offer.name.charAt(0)}</div>
        )}
      </div>
      <div className={styles.offerInfo}>
        <div className={styles.offerName}>{offer.name}</div>
        <div className={styles.offerReward}>
          {getOfferRewardDisplay(offer.rewardType, offer.rewardValue)}
        </div>
      </div>
    </div>
  );
};

export default memo(CardOfferTile);
